<script lang="ts" module>
	import { attrs } from '#library/attrs.ts'
	import type { Blok, Acast } from '#library/components.schema.build.ts'
	import { format_date } from 'kitto'
	import { SvelteSet, SvelteURL } from 'svelte/reactivity'
	import type { AcastawayShow } from 'moxyloco/acastaway-types'
	import Pagination from '#components/pagination.svelte'
	import { goto } from '$app/navigation'
	import { params } from '#library/params.svelte.ts'
	import { scrollto } from '#library/scrollto.ts'

	/** What this block's `load` attaches to the blok at request time. */
	type AcastData = {
		items?: AcastawayShow['data']['items']
		pagination?: { page: number; per_page: number; total: number }
	}

	interface Props {
		blok: Blok<Acast> & AcastData
	}

	const ACAST_ID = '67dd6472ec9837e25e75d7a4'

	const PER_PAGE = 4

	// results go on the blok, not module scope: module state is shared by every
	// request the server process handles, so with `page` read off the URL one
	// visitor's page of episodes was served to everyone. `story` is per-request.
	export const load: Load = async ({ story, event }) => {
		if (!('blocks' in story.content)) return

		const blocks = story.content.blocks?.filter(b => b.component === 'acast') as Array<AcastData> | undefined
		if (!blocks?.length) return

		const page = parseInt(event.url.searchParams.get('page') ?? '1')

		try {
			const url = new SvelteURL('https://moxy.uilo.co')
			url.pathname = `/api/acastaway/${ACAST_ID}`
			url.searchParams.set('limit', PER_PAGE.toString())
			url.searchParams.set('page', page.toString())

			const response = await fetch(url)
			const json = (await response.json()) as AcastawayShow

			for (const block of blocks) {
				block.items = json.data.items
				block.pagination = { page, per_page: PER_PAGE, total: json.meta.total }
			}
		} catch (error) {
			console.error(error)
		}
	}
</script>

<script lang="ts">
	let { blok }: Props = $props()

	let section: HTMLElement | undefined = $state()
	let clamps = new SvelteSet<string>()
	let expanded_items = $state<Array<string>>([])

	function toggle(value: string) {
		if (expanded_items.includes(value)) {
			expanded_items = expanded_items.filter(v => v !== value)
		} else {
			expanded_items.push(value)
		}
	}

	async function go(p: Record<string, string | number | null>): Promise<void> {
		await goto(params(p), { replace: true, reset: false })
		scrollto(blok.anchor!)
	}

	function clamped(node: HTMLElement, onchange: (is: boolean) => void) {
		const ro = new ResizeObserver(() => onchange(node.scrollHeight > node.clientHeight + 1))

		ro.observe(node)

		queueMicrotask(() => onchange(node.scrollHeight > node.clientHeight + 1))

		return {
			update(fn: (is: boolean) => void) {
				onchange = fn
			},
			destroy() {
				ro.disconnect()
			}
		}
	}
</script>

{#if blok.items?.length}
	<section class="acast {blok.theme}" {...attrs(blok)} bind:this={section}>
		<div class="contain">
			{#if blok.title}
				<div class="title">{blok.title}</div>
			{/if}
			<div class="items">
				{#each blok.items ?? [] as item (item.id)}
					{@const expanded = expanded_items.includes(item.id)}
					<article class="item" class:expanded>
						<header class="header">
							<span class="name">{item.title}</span>
							<span class="date-duration">
								<time class="date-duration" datetime={item.published}>
									{format_date('{MMMM} {D}', item.published)}
								</time>, {item.itunes.duration}
							</span>
							<span
								class="description"
								use:clamped={is => (is || expanded ? clamps.add(item.id) : clamps.delete(item.id))}
							>
								{@html item.description}
							</span>
							{#if clamps.has(item.id)}
								<button class="show-more" onclick={() => toggle(item.id)}>
									{expanded ? 'Show less' : 'Show more'}
									<svg class="arrow">
										<use xlink:href="/images/vector.svg#chevron-up"></use>
									</svg>
								</button>
							{/if}
						</header>
						<iframe
							title="{item.title} - {format_date('{MMMM} {D}', item.published)}"
							src="https://embed.acast.com/{ACAST_ID}/{item.id}"
							scrolling="no"
							class="embed"
						></iframe>
					</article>
				{/each}
			</div>
			<div class="pagi">
				<Pagination
					active={(blok.pagination?.page ?? 1) - 1}
					items={Math.ceil((blok.pagination?.total ?? 0) / (blok.pagination?.per_page ?? 1))}
					onprev={() => go({ page: (blok.pagination?.page ?? 1) - 1 })}
					onnext={() => go({ page: (blok.pagination?.page ?? 1) + 1 })}
					onpage={page => go({ page: page + 1 })}
					numbers
				/>
			</div>
		</div>
	</section>
{/if}

<style lang="css">
	.acast {
		padding-block: var(--padding-top, var(--padding-default)) var(--padding-bottom, var(--padding-default));
	}

	.title {
		color: var(--c-foreground);
		font: 600 var(--f-h2);
		letter-spacing: var(--ls-h2);
		text-wrap: balance;
	}

	.items {
		display: grid;
		gap: 4rem;
		margin-block: 4rem;
	}

	:global(.description a) {
		font-weight: 500;
	}

	.item {
		display: grid;
		gap: 2rem;

		&:not(.expanded) .description {
			display: -webkit-box;
			-webkit-line-clamp: 2;
			-webkit-box-orient: vertical;
			overflow: hidden;
		}

		&.expanded .arrow {
			rotate: 0deg;
		}
	}

	.header {
		display: grid;

		@media (--from-mobile) {
			grid-template-columns: 3fr 1fr;
			gap: 0.5rem 2rem;
		}
	}

	.name {
		font: 700 var(--f-h5);
		letter-spacing: var(--ls-h5);
	}

	.date-duration {
		font: 500 var(--f-body2);
		letter-spacing: var(--ls-body2);
		text-transform: uppercase;
		justify-self: end;
	}

	.embed {
		aspect-ratio: unset;
	}

	.show-more {
		grid-column: 1;
		text-align: start;
		font: 500 var(--f-body1);
		display: flex;
		gap: 0.5rem;
	}

	.arrow {
		rotate: 180deg;
		size: 1.5em;
		transition: rotate 0.3s ease;
	}

	.pagi {
		display: flex;
		justify-content: center;
	}
</style>
